import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { userApi, adminApi, USER_TOKEN, ADMIN_TOKEN } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      const tasks = [];
      if (localStorage.getItem(USER_TOKEN)) {
        tasks.push(userApi.get("/auth/me").then((r) => setUser(r.data.user)).catch(() => localStorage.removeItem(USER_TOKEN)));
      }
      if (localStorage.getItem(ADMIN_TOKEN)) {
        tasks.push(adminApi.get("/auth/me").then((r) => setAdmin(r.data.user)).catch(() => localStorage.removeItem(ADMIN_TOKEN)));
      }
      await Promise.all(tasks);
      setReady(true);
    })();
  }, []);

  // Applies a token response, or returns the OTP challenge when a code is required
  const apply = useCallback((data, scope) => {
    if (data.otp_required) return { challenge: data };
    localStorage.setItem(scope === "admin" ? ADMIN_TOKEN : USER_TOKEN, data.token);
    (scope === "admin" ? setAdmin : setUser)(data.user);
    return { user: data.user };
  }, []);

  const login = useCallback(async (email, password) => apply((await userApi.post("/auth/login", { email, password })).data, "user"), [apply]);
  const register = useCallback(async (payload) => apply((await userApi.post("/auth/register", payload)).data, "user"), [apply]);
  const adminLogin = useCallback(async (email, password) => {
    const device_token = localStorage.getItem("admin_device_token") || undefined;
    return apply((await adminApi.post("/auth/admin/login", { email, password, device_token })).data, "admin");
  }, [apply]);
  const verifyOtp = useCallback(async (challenge_id, code, scope = "user", opts = {}) => {
    const r = await userApi.post("/auth/otp/verify", { challenge_id, code, trust_device: !!opts.trustDevice });
    if (r.data?.trusted_device?.device_token) {
      localStorage.setItem("admin_device_token", r.data.trusted_device.device_token);
    }
    return apply(r.data, scope);
  }, [apply]);
  const refreshUser = useCallback(async () => { const r = await userApi.get("/auth/me"); setUser(r.data.user); }, []);

  const logout = useCallback(() => { localStorage.removeItem(USER_TOKEN); setUser(null); }, []);
  const adminLogout = useCallback(() => { localStorage.removeItem(ADMIN_TOKEN); setAdmin(null); }, []);

  return (
    <AuthCtx.Provider value={{ user, admin, ready, login, register, adminLogin, verifyOtp, refreshUser, logout, adminLogout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
